// @flow

import { store } from '../../network/reduce';
import shoppingCenterApi from '../../network/api/shoppingCenterApi';
import {
  FILTER_SHOPPING_CENTER_LIST,
  FILTER_SHOPPING_CENTER_REQUEST,
  FILTER_SHOPPING_CENTER_STORE_REQUEST,
  SAVE_ASSET_FEATURE_LIST,
  SAVE_BRANCH_LEVEL,
  SAVE_PRACTICAL_INFO,
  SAVE_RECONCILIATION_LIST,
  SAVE_SHOPPING_CENTER_LIST,
  SAVE_SHOPPING_CENTER_MEDIAS,
  SAVE_STORE_LIST,
  SAVE_USER_GEMINI_RIGHTS,
  SHOPPING_CENTER_APPLIED_FILTERS,
} from './ShoppingCentersReducer';
import { filterShoppingCenter } from './shoppingCenterFilter';
import type {
  BranchLevels,
  Filter,
  GeminiRights,
  PracticalInfos,
  ReconciliationStore,
  ShoppingCenter,
} from '../../types/shoppingCenterType';
import type { MediaGallery } from '../../types/keepeekType';
import { IS_MEDIA_LOADING } from '../page/pageReducer';
import geminiApi from '../../network/api/geminiApi';

export const fetchShoppingCenters = (): Function =>
  (dispatch: () => void) => shoppingCenterApi.list()
    .then(response => response.json())
    .then((response) => {
      dispatch(saveBaseShoppingCenterList(response));
      dispatch(saveFilteredShoppingCenterList(response));
      dispatch(saveAssetFeatures(getAssetFeatures(response)));
      dispatch(saveStores(getStores(response)));
    }).catch(console.error);

export const fetchBranchLevel = (shoppingCenterId: string): Function =>
  (dispatch: () => void) => shoppingCenterApi.fetchBranchLevel(shoppingCenterId)
    .then(result => result.json())
    .then(json => dispatch(saveBranchLevel(json)))
    .catch(console.error);

export const fetchPracticalInfos = (shoppingCenterId: string): Function =>
  (dispatch: () => void) => shoppingCenterApi.fetchPracticalInfos(shoppingCenterId)
    .then(resp => resp.json())
    .then(json => dispatch(savePracticalInfos(json)))
    .catch(console.error);


export const dischargeMedia = (): Function =>
  (dispatch: () => void) => {
    dispatch(saveShoppingCenterMedias([]));
    dispatch(store(IS_MEDIA_LOADING, true));
  };

export const fetchShoppingCenterMedia = (shoppingCenterId: string): Function =>
  (dispatch: () => void) =>
    shoppingCenterApi.fetchMedias(shoppingCenterId)
      .then(response => response.json())
      .then((response: MediaGallery[]) => {
        dispatch(saveShoppingCenterMedias(response));
      })
      .catch(console.error)
      .finally(() => {
        dispatch(store(IS_MEDIA_LOADING, false));
      });

export const fetchGeminiRights = (pcId: string): Function =>
  (dispatch: () => void) =>
    geminiApi.fetchAuthorized(pcId)
      .then(res => res.json())
      .then((geminiRights: GeminiRights) => {
        dispatch(saveGeminiRights(geminiRights));
      })
      .catch(console.error);

const getAssetFeatures = (shoppingCenterList: ShoppingCenter[]): string[] => {
  const assetFeatureSet = new Set();
  for (let i = 0; i < shoppingCenterList.length; ++i) {
    for (let y = 0; y < shoppingCenterList[i].assetFeatures.length; ++y) {
      assetFeatureSet.add(shoppingCenterList[i].assetFeatures[y].label);
    }
  }
  return [...assetFeatureSet];
};

const getStores = (shoppingCenterList: ShoppingCenter[]): string[] => {
  const storeSet = new Set();
  for (let i = 0; i < shoppingCenterList.length; ++i) {
    for (let y = 0; y < shoppingCenterList[i].storeList.length; ++y) {
      storeSet.add(shoppingCenterList[i].storeList[y]);
    }
  }
  return [...storeSet];
};

export const applyFilters = (filters: Filter[], search: string): Function =>
  (dispatch: () => void, getState: Function) => {
    const filteredSCList = filterShoppingCenter(
      getState().shoppingCenterFilters.baseShoppingCenterList,
      filters,
      search,
    );
    dispatch(saveFilteredShoppingCenterList(filteredSCList));
    dispatch(saveFilteredShoppingCenterRequest(search));
    dispatch(saveShoppingCenterAppliedFilters(filters));
  };

export const saveFilteredShoppingCenterList = (shoppingCenterList: ShoppingCenter[]): Function =>
  (dispatch: () => void) => {
    dispatch(store(FILTER_SHOPPING_CENTER_LIST, shoppingCenterList));
  };

export const saveFilteredShoppingCenterRequest = (shoppingCenterRequest: string): Function =>
  (dispatch: () => void) => {
    dispatch(store(FILTER_SHOPPING_CENTER_REQUEST, shoppingCenterRequest));
  };

export const saveFilteredStoreRequest = (shoppingCenterRequest: string): Function =>
  (dispatch: () => void) => {
    dispatch(store(FILTER_SHOPPING_CENTER_STORE_REQUEST, shoppingCenterRequest));
  };

export const saveShoppingCenterAppliedFilters = (shoppingCenterAppliedFilters: Filter[]): Function =>
  (dispatch: () => void) => {
    dispatch(store(SHOPPING_CENTER_APPLIED_FILTERS, shoppingCenterAppliedFilters));
  };

export const saveBaseShoppingCenterList = (shoppingCenterList: ShoppingCenter[]): Function =>
  (dispatch: () => void) => {
    dispatch(store(SAVE_SHOPPING_CENTER_LIST, shoppingCenterList));
  };

export const saveShoppingCenterMedias = (shoppingCenterMedia: MediaGallery[]): Function =>
  (dispatch: () => void) => {
    dispatch(store(SAVE_SHOPPING_CENTER_MEDIAS, shoppingCenterMedia));
  };

const saveAssetFeatures = (assetFeatures: string[]): Function =>
  (dispatch: () => void) => {
    dispatch(store(SAVE_ASSET_FEATURE_LIST, assetFeatures));
  };

const saveStores = (stores: string[]): Function =>
  (dispatch: () => void) => {
    dispatch(store(SAVE_STORE_LIST, stores));
  };

const saveBranchLevel = (branchLevels: BranchLevels[]): Function =>
  (dispatch: () => void) => {
    dispatch(store(SAVE_BRANCH_LEVEL, branchLevels));
  };

export const saveReconciliationList = (reconciliationList: ReconciliationStore[]): Function =>
  (dispatch: () => void) => {
    dispatch(store(SAVE_RECONCILIATION_LIST, reconciliationList));
  };


const savePracticalInfos = (practicalInfos: PracticalInfos): Function =>
  (dispatch: () => void) => {
    dispatch(store(SAVE_PRACTICAL_INFO, practicalInfos));
  };

const saveGeminiRights = (geminiRights: GeminiRights): Function =>
  (dispatch: () => void) => dispatch(store(SAVE_USER_GEMINI_RIGHTS, geminiRights));
